import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import useTextMeasurer from 'shared/hooks/use-text-measurer';
import useWindowResize from 'shared/hooks/use-window-resize';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import {
  gray1,
  gray3,
  gray5,
  gray6,
  gray7,
  primary,
  white,
} from 'styles/global_defaults/colors';
import {
  openSans,
  semiBoldFontWeight,
  textMediumFontSize,
  textSmallFontSize,
} from 'styles/global_defaults/fonts';
import {
  halfSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { handheld } from 'styles/global_defaults/media-queries';
import { NvFilter, NvFilterProps } from './nv-filter';
import { novoEdBlue } from 'athena/styles/colors';
import { useMediaQuery } from 'react-responsive';

export type FiltersData = NvFilterProps;

type FiltersBarProps = {
  filters: Array<FiltersData>;
  hasFiltersSelectedOrApplied: boolean;
  onApplyFilters: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onClearFilters: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  className?: string;
  resetDataQa?: string;
  applyFiltersDataQa?: string;
  expandFiltersListDataQa?: string;
  collapseFiltersListDataQa?: string;
  maxFilterWidth?: number;
};

const COLUMN_LAYOUT_MIN_WIDTH = 415;

const styles = css`
  width: 100%;
  min-height: 35px;
  padding: ${halfSpacing}px ${standardSpacing}px;
  background: ${gray7};
  border-bottom: 1px solid ${gray6};
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: ${standardSpacing}px;

  ${handheld(css`
    height: initial;
    padding: ${halfSpacing}px ${standardSpacing}px;
    gap: ${halfSpacing}px;
  `)};

  .filters {
    position: relative;
    white-space: nowrap;
    font-family: ${openSans};
    gap: ${halfSpacing}px;
    min-width: 0;
    overflow: visible;
    flex-shrink: 1;
    flex-wrap: wrap;

    &.collapsed {
      max-height: ${tripleSpacing}px;
    }
    &.expanded {
      height: auto;
    }

    div.bs4-dropdown {
      border: none;

      div[role='button'] {
        overflow: hidden;
        border-radius: inherit;
        height: 30px;
        padding: ${halfSpacing}px;
        border: 1px solid ${gray6};
        border-radius: ${threeQuartersSpacing}px;
        gap: ${quarterSpacing}px;

        .icon {
          color: ${gray3};
          transition: transform 0.218s ease-in;
        }

        &:hover .icon {
          color: ${primary};
        }

        &.selected {
          color: ${white};
          border: 1px solid ${primary};
          background: ${primary};
          .icon {
            color: ${white};
          }
        }
      }

      &.show div[role='button'] .icon {
        transform: rotate(-180deg);
      }

      .bs4-dropdown-menu.filter-menu.show {
        min-width: 200px;
        max-height: 400px;
        overflow: auto;
        padding: ${halfSpacing}px ${standardSpacing}px;
        border-radius: 4px;
        border: 1px solid ${gray5};
        box-shadow: 1px 2px 2px rgba(0, 0, 0, 0.1);
        display: flex !important;
        flex-direction: column;
        gap: ${halfSpacing}px;
      }

      .title {
        color: ${gray1};
      }
      &:hover .title {
        color: ${primary};
      }

      .nv-dropdown-header.title {
        height: initial;
        margin: 0;
        padding: 0;
        font-weight: ${semiBoldFontWeight};
        color: ${gray1};
      }

      .nv-checkbox-item.filter-item {
        padding: 0;

        label {
          margin-bottom: 0;
        }
      }
    }
  }

  .toggle-btn {
    margin-left: ${halfSpacing}px;
    font-size: ${textMediumFontSize}px;
    color: ${novoEdBlue};
    background: none;
    border: none;
    cursor: pointer;
    font-weight: ${semiBoldFontWeight};
    padding: 0;
  }

  .buttons {
    gap: ${halfSpacing}px;
    button {
      padding: ${quarterSpacing}px ${threeQuartersSpacing}px;
      font-weight: ${semiBoldFontWeight};
      line-height: 24px;
    }
    .bs4-btn[disabled],
    .bs4-btn-primary[disabled] {
      opacity: initial;
      color: ${gray3};
      font-weight: ${semiBoldFontWeight};
    }
  }
`;

const applyFiltersStyles = css`
  box-shadow: none;
  transition: background-color 0.3s ease-in-out;
`;

export const NvFiltersBar = ({
  filters,
  hasFiltersSelectedOrApplied,
  onApplyFilters,
  onClearFilters,
  className,
  resetDataQa,
  applyFiltersDataQa,
  expandFiltersListDataQa,
  collapseFiltersListDataQa,
  maxFilterWidth,
}: FiltersBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(filters.length);
  const isColumnLayout = useMediaQuery({ query: `(max-width: ${COLUMN_LAYOUT_MIN_WIDTH}px)` });

  // Use text measurer for more accurate filter width
  const textStyles = {
    fontSize: textSmallFontSize,
    fontFamily: openSans,
    fontWeight: semiBoldFontWeight,
  };
  const measureText = useTextMeasurer(textStyles);
  const BUTTONS_WIDTH = 165;

  const calculateFittingFilters = useCallback(() => {
    if (expanded) {
      setVisibleCount(filters.length);
      return;
    }
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const availableWidth = containerWidth - BUTTONS_WIDTH;
      let usedWidth = 0;
      let count = 0;
      for (let i = 0; i < filters.length; i++) {
        // Measure text width and add 48px for padding, icon, margin, gap
        const textWidth = measureText(filters[i].title) + 48;
        const labelWidth = maxFilterWidth ? Math.min(textWidth, maxFilterWidth) : textWidth;
        if (usedWidth + labelWidth > availableWidth) break;
        usedWidth += labelWidth;
        count++;
      }
      setVisibleCount(count > 0 ? count : 1);
    }
  }, [filters, expanded, measureText, maxFilterWidth]);

  useLayoutEffect(() => {
    calculateFittingFilters();
  }, [filters.length, expanded, calculateFittingFilters]);

  useWindowResize(
    () => {
      calculateFittingFilters();
    },
    undefined,
    false,
    [calculateFittingFilters]
  );

  const handleOnApplyFilters = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    onApplyFilters(e);
    e.currentTarget.blur();
  };

  const visibleFilters = filters.slice(0, visibleCount);
  const overflowFilters = filters.slice(visibleCount);
  const hiddenCount = overflowFilters.length;

  return (
    <section
      css={styles}
      className={`${className} ${isColumnLayout ? 'flex-column align-items-stretch' : ''}`}>
      <div
        className={`d-flex filters align-items-center ${expanded ? 'expanded' : 'collapsed'}`}
        ref={containerRef}
      >
        {(expanded ? filters : visibleFilters).map((filter) => (
          <NvFilter
            key={filter.title}
            title={filter.title}
            selected={filter.selected}
            items={filter.items}
            onToggle={filter.onToggle}
          />
        ))}
        {hiddenCount > 0 && !expanded && (
          <Button
            variant='link'
            className='toggle-btn'
            onClick={() => setExpanded(true)}
            data-qa={expandFiltersListDataQa}
          >
            {t.SHARED.COUNT_MORE(hiddenCount)}
          </Button>
        )}
        {expanded && (
          <Button
            variant='link'
            className='toggle-btn'
            onClick={() => setExpanded(false)}
            data-qa={collapseFiltersListDataQa}
          >
            {t.SHARED.SHOW_LESS()}
          </Button>
        )}
      </div>
      <div className='d-flex buttons align-self-start'>
        <Button
          variant='link'
          className='semi-bold px-0'
          disabled={!hasFiltersSelectedOrApplied}
          onClick={onClearFilters}
          data-qa={resetDataQa}
        >
          {t.SHARED.RESET()}
        </Button>
        <Button
          variant='primary'
          css={applyFiltersStyles}
          disabled={!hasFiltersSelectedOrApplied}
          onClick={handleOnApplyFilters}
          data-qa={applyFiltersDataQa}
        >
          {t.SHARED.APPLY_FILTERS()}
        </Button>
      </div>
    </section>
  );
};

export default NvFiltersBar;
